import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';

import { BaseAutomationDiscoveryDialog } from './BaseAutomationDiscoveryDialog';
import { EventSourceName } from './utils/analytics';
import { TimeSavedMessage } from './productivity-insights/TimeSavedMessage';

const i18n = defineMessages({
	spaceAssignedHeaderBulk: {
		id: 'automation-discovery.space-assigned-bulk.header',
		defaultMessage:
			'{guestCount, plural, one {# guest } other {# guests}} assigned to space {spaceName}',
		description:
			'A header telling the user a certain number of guests were assigned to a specific space',
	},
	spaceAssignedHeaderSingle: {
		id: 'automation-discovery.space-assigned-single.header',
		defaultMessage: 'Space access changed',
		description: 'A header telling the user space access was changed for a single guest user',
	},
	defaultSpaceAssignedHeader: {
		id: 'automation-discovery.default-space-assigned.header',
		defaultMessage: 'Default space updated',
		description: 'A header telling the user that the default space for guests was updated',
	},
	ctaText: {
		id: 'automation-discovery.space-assigned.cta.text',
		defaultMessage: 'Add all previously unassigned guests to this space?',
		description:
			'A header asking the user if they want to automatically add future guests to a specific space',
	},
});

type SpaceAssignedAutomationDiscoveryDialogProps = {
	onClose: () => void;
	localStorageKey: string;
	guestCount?: number;
	spaceName?: string;
	eventSource: EventSourceName;
};

const getHeader = (eventSource: EventSourceName, guestCount?: number, spaceName?: string) => {
	switch (eventSource) {
		case EventSourceName.DEFAULT_SPACE_ASSIGN:
			return <FormattedMessage {...i18n.defaultSpaceAssignedHeader} />;
		case EventSourceName.BULK_ASSIGN:
			return (
				<FormattedMessage
					{...i18n.spaceAssignedHeaderBulk}
					values={{
						guestCount,
						spaceName,
					}}
				/>
			);
		case EventSourceName.SINGLE_ASSIGN:
		default:
			return <FormattedMessage {...i18n.spaceAssignedHeaderSingle} />;
	}
};

const SpaceAssignedAutomationDiscoveryDialogComponent = ({
	onClose,
	localStorageKey,
	guestCount,
	spaceName,
	eventSource,
}: SpaceAssignedAutomationDiscoveryDialogProps) => {
	const PromptInsightMessage = <TimeSavedMessage timeSaved="258" />;
	return (
		<BaseAutomationDiscoveryDialog
			eventSourceName={eventSource}
			templateName="scheduledInactivePagesDisablePublicLink"
			discoveryExperience="automationDiscoverySpaceAssigned"
			onClose={onClose}
			cta={<FormattedMessage {...i18n.ctaText} />}
			insightMessage={PromptInsightMessage}
			header={getHeader(eventSource, guestCount, spaceName)}
			localStorageKey={localStorageKey}
			templateUrl="/wiki/admin/automation#/rule/template?templateId=confluence_collaborate_template_13"
		/>
	);
};

export const SpaceAssignedAutomationDiscoveryDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(SpaceAssignedAutomationDiscoveryDialogComponent);
